import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { formatCurrency, formatDate } from "@/utils/format";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { ConvexError } from "convex/values";
import { DEMO_IDS } from "@/mock/demo";
import { Id } from "@cvx/_generated/dataModel";
import { Trash2 } from "lucide-react";
import { api } from "~/convex/_generated/api";
import { createFileRoute } from "@tanstack/react-router";
import { useAccount } from "@/state/useAccount";
import { useToast } from "@/components/ui/use-toast";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/creator-invites",
)({
  component: CreatorInvitesSettings,
  beforeLoad: () => ({
    title: "Creator Invites",
    headerTitle: "Creator Invitations",
    headerDescription: "Manage your sent creator invitations.",
  }),
});

export default function CreatorInvitesSettings() {
  const { appId, demoModeEnabled } = useAccount();
  const { toast } = useToast();

  // Check if we're in demo mode
  const isDemo =
    demoModeEnabled &&
    (appId as unknown as string) === (DEMO_IDS.appId as unknown as string);

  const { data: invites } = useQuery(
    convexQuery(api.invites.getCreatorInvites, { appId }),
  );

  // If in demo mode, return early with demo message
  if (isDemo) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p className="text-sm text-primary/60">
          Demo mode - no creator invitations to display
        </p>
      </div>
    );
  }

  const { mutateAsync: deleteInvite, isPending: isDeleting } = useMutation({
    mutationFn: useConvexMutation(api.invites.deleteCreatorInvite),
  });

  if (!appId) {
    return null;
  }

  const handleDeleteInvite = async (inviteId: Id<"creatorInvites">) => {
    try {
      await deleteInvite({ inviteId });
      toast({
        title: "Invitation deleted",
        description: "The creator invitation has been deleted successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof ConvexError
            ? error.data
            : "Failed to delete the invitation. Please try again.",
        variant: "destructive",
      });
    }
  };

  const pendingInvites =
    invites?.filter((invite) => invite.status === "pending") || [];
  const respondedInvites =
    invites?.filter((invite) => invite.status !== "pending") || [];

  if (!invites || invites.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p className="text-sm text-primary/60">No creator invitations sent</p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-6">
      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
          <div className="flex flex-col gap-2 p-6 w-full">
            <h2 className="text-xl font-medium text-primary">
              Pending Invitations
            </h2>
            <div className="mt-4 space-y-4">
              {pendingInvites.map((invite) => (
                <div
                  key={invite._id}
                  className="flex flex-col gap-4 rounded-lg border border-border p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex flex-col">
                      <span className="text-xs text-primary/60">
                        {invite.campaignName}
                      </span>
                      <span className="text-sm font-medium text-primary mt-2">
                        {invite.inviteeEmail}
                      </span>
                      <span className="text-xs text-primary/60">
                        Sent on{" "}
                        {formatDate(
                          new Date(invite._creationTime).toISOString(),
                        )}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive/90"
                      onClick={() => handleDeleteInvite(invite._id)}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-4 text-xs text-primary/60">
                    <div>
                      <span className="font-medium">Contract Period:</span>
                      <div>
                        {formatDate(new Date(invite.startDate).toISOString())} -{" "}
                        {formatDate(new Date(invite.endDate).toISOString())}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">Required Videos:</span>
                      <div>
                        {invite.requiredVideos?.count} video
                        {invite.requiredVideos?.count !== 1 ? "s" : ""}
                        {invite.requiredVideos?.duration &&
                        Math.abs(
                          (invite.endDate - invite.startDate) /
                            (1000 * 60 * 60 * 24) -
                            invite.requiredVideos.duration,
                        ) <= 1
                          ? " for the duration of the contract"
                          : ` every ${invite.requiredVideos?.duration} days`}
                      </div>
                      <div className="text-xs text-primary/60 italic mt-1">
                        Note: This is the total number of videos you need to
                        publish across all platforms.
                      </div>
                    </div>

                    {/* Flat Fee Per Video */}
                    {invite.flatFee?.perVideo && (
                      <div>
                        <span className="font-medium">Flat Fee per Video:</span>
                        <div>
                          {formatCurrency(invite.flatFee?.perVideo || 0)}
                        </div>
                      </div>
                    )}

                    {/* Time Period Fee (Monthly/Recurring) */}
                    {invite.flatFee?.timePeriod && (
                      <div>
                        <span className="font-medium">
                          {Math.abs(
                            (invite.endDate - invite.startDate) /
                              (1000 * 60 * 60 * 24) -
                              invite.flatFee.timePeriod.days,
                          ) <= 1
                            ? "One Time Payment:"
                            : `Flat Fee per ${invite.flatFee.timePeriod.days} days:`}
                        </span>
                        <div>
                          {formatCurrency(invite.flatFee.timePeriod.amount)}
                        </div>
                      </div>
                    )}

                    {/* CPM Rate */}
                    {invite.cpmRate?.rate && (
                      <div>
                        <span className="font-medium">
                          CPM Rate
                          {invite.cpmRate?.type === "totalViews" &&
                            " (Total Views)"}
                          {invite.cpmRate?.type === "perVideo" &&
                            " (Per Video)"}
                          :
                        </span>
                        <div>
                          {formatCurrency(invite.cpmRate?.rate || 0, 3)}
                        </div>
                      </div>
                    )}

                    {/* Minimum Views Required */}
                    {invite.cpmRate?.threshold && (
                      <div>
                        <span className="font-medium">
                          Minimum Views Required
                          {invite.cpmRate?.type === "totalViews" && " (Total)"}
                          {invite.cpmRate?.type === "perVideo" &&
                            " (Per Video)"}
                          :
                        </span>
                        <div>
                          {invite.cpmRate?.threshold?.toLocaleString()} views
                        </div>
                      </div>
                    )}

                    {/* Maximum Payment Cap */}
                    {invite.cpmRate?.maxPayout && (
                      <div>
                        <span className="font-medium">
                          Maximum Payment Cap
                          {invite.cpmRate?.type === "totalViews" && " (Total)"}
                          {invite.cpmRate?.type === "perVideo" &&
                            " (Per Video)"}
                          :
                        </span>
                        <div>
                          {formatCurrency(invite.cpmRate?.maxPayout || 0)}
                        </div>
                      </div>
                    )}

                    {/* Cutoff Period */}
                    {invite.cpmRate?.cutoffPeriod && (
                      <div>
                        <span className="font-medium">CPM Cutoff Period:</span>
                        <div>
                          {invite.cpmRate.cutoffPeriod} days after{" "}
                          {invite.cpmRate.type === "totalViews"
                            ? "contract ends"
                            : "each video is posted"}
                        </div>
                        {invite.cpmRate.type === "totalViews" && (
                          <div className="text-xs mt-1">
                            Cutoff Date:{" "}
                            {formatDate(
                              new Date(
                                invite.endDate +
                                  invite.cpmRate.cutoffPeriod *
                                    24 *
                                    60 *
                                    60 *
                                    1000,
                              ).toISOString(),
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {invite.customMessage && (
                    <div className="text-xs text-primary/60">
                      <span className="font-medium">Message:</span>
                      <div className="mt-1 italic">
                        "{invite.customMessage}"
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Responded Invitations */}
      {respondedInvites.length > 0 && (
        <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
          <div className="flex flex-col gap-2 p-6 w-full">
            <h2 className="text-xl font-medium text-primary">
              Responded Invitations
            </h2>
            <div className="mt-4 space-y-4">
              {respondedInvites.map((invite) => (
                <div
                  key={invite._id}
                  className="flex items-center justify-between rounded-lg border border-border p-4"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-primary">
                      {invite.inviteeEmail}
                    </span>
                    <span className="text-xs text-primary/60">
                      {invite.status === "accepted" ? "Accepted" : "Declined"}{" "}
                      on{" "}
                      {invite.respondedAt &&
                        formatDate(new Date(invite.respondedAt).toISOString())}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-3 py-1 text-xs font-medium rounded-full ${
                        invite.status === "accepted"
                          ? "bg-green-500/10 text-green-500"
                          : "bg-red-500/10 text-red-500"
                      }`}
                    >
                      {invite.status === "accepted" ? "Accepted" : "Declined"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
